import { FormField, inputClass, selectClass } from "../components/FormField";
import { cn } from "@/shared/lib/cn";

export type InvoiceCurrencyCode = "USD" | "CDF";

export function currencySuffix(currency: InvoiceCurrencyCode): string {
  return currency === "CDF" ? "FC" : "USD";
}

export function formatInvoiceAmount(
  value: string | number,
  currency: InvoiceCurrencyCode = "USD",
): string {
  if (value === "" || value == null) return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  if (currency === "CDF") {
    return `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)} FC`;
  }
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

export function isValidExchangeRateCdf(value: string): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n >= 100 && n <= 50000;
}

type CurrencyAmountInputProps = {
  value: string;
  onChange: (value: string) => void;
  currency: InvoiceCurrencyCode;
  placeholder?: string;
  disabled?: boolean;
};

export function CurrencyAmountInput({
  value,
  onChange,
  currency,
  placeholder = "0.00",
  disabled = false,
}: CurrencyAmountInputProps) {
  return (
    <div className="flex">
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={cn(inputClass, "rounded-r-none border-r-0 disabled:opacity-60")}
      />
      <span className="flex h-10 items-center rounded-r-lg border border-border bg-card-muted px-3 text-sm font-medium text-muted-foreground">
        {currencySuffix(currency)}
      </span>
    </div>
  );
}

type InvoiceCurrencyFieldsProps = {
  currency: InvoiceCurrencyCode;
  exchangeRateCdf: string;
  onCurrencyChange: (value: InvoiceCurrencyCode) => void;
  onExchangeRateChange: (value: string) => void;
  labels: {
    currency: string;
    currencyHint?: string;
    exchangeRate: string;
    exchangeRateHint?: string;
    currencyUsd: string;
    currencyCdf: string;
  };
};

export function InvoiceCurrencyFields({
  currency,
  exchangeRateCdf,
  onCurrencyChange,
  onExchangeRateChange,
  labels,
}: InvoiceCurrencyFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField label={labels.currency} required hint={labels.currencyHint}>
        <select
          value={currency}
          onChange={(e) => onCurrencyChange(e.target.value as InvoiceCurrencyCode)}
          className={selectClass}
        >
          <option value="USD">{labels.currencyUsd}</option>
          <option value="CDF">{labels.currencyCdf}</option>
        </select>
      </FormField>
      {currency === "CDF" && (
        <FormField label={labels.exchangeRate} required hint={labels.exchangeRateHint}>
          <div className="flex">
            <input
              type="number"
              min={100}
              max={50000}
              step="0.01"
              value={exchangeRateCdf}
              onChange={(e) => onExchangeRateChange(e.target.value)}
              placeholder="2800"
              className={cn(inputClass, "rounded-r-none border-r-0")}
            />
            <span className="flex h-10 items-center rounded-r-lg border border-border bg-card-muted px-3 text-sm font-medium text-muted-foreground">
              FC/USD
            </span>
          </div>
        </FormField>
      )}
    </div>
  );
}
